/**
 * Timezone utility for frontend to handle Singapore time (GMT+8)
 * This ensures consistent date/time display across the application
 */

// Singapore timezone identifier
const SINGAPORE_TZ = 'Asia/Singapore';

/**
 * Format a date string or Date object for Singapore timezone display
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted date string in Singapore timezone
 */
export function formatSingaporeDate(
  date: string | Date, 
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }
): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-SG', {
      ...options,
      timeZone: SINGAPORE_TZ
    });
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'Invalid Date';
  }
}

/**
 * Format a date string or Date object for Singapore timezone time display
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted time string in Singapore timezone
 */
export function formatSingaporeTime(
  date: string | Date, 
  options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }
): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleTimeString('en-SG', {
      ...options,
      timeZone: SINGAPORE_TZ
    });
  } catch (error) {
    console.warn('Error formatting time:', error);
    return 'Invalid Time';
  }
}

/**
 * Format a date string or Date object for Singapore timezone full datetime display
 * @param date - Date string or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted datetime string in Singapore timezone
 */
export function formatSingaporeDateTime(
  date: string | Date, 
  options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }
): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-SG', {
      ...options,
      timeZone: SINGAPORE_TZ
    });
  } catch (error) {
    console.warn('Error formatting datetime:', error);
    return 'Invalid DateTime';
  }
}

/**
 * PostgREST range bounds for a Singapore calendar day, for filtering a
 * timestamptz column. A bare "yyyy-MM-dd" is read as midnight UTC, which is
 * 08:00 in Singapore: `lte.<end day>` then dropped everything after 08:00 on
 * the last day, and a one-day range matched nothing.
 * @param day - A Singapore calendar day, "yyyy-MM-dd"
 * @returns The first instant of that day and of the next one, both +08:00
 */
export function singaporeDayBounds(day: string): { start: string; nextDayStart: string } {
  const [year, month, date] = day.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, date + 1)).toISOString().slice(0, 10);
  return {
    start: `${day}T00:00:00+08:00`,
    nextDayStart: `${next}T00:00:00+08:00`,
  };
}

 